import * as THREE from 'three';
import StraightStair from '../components/staircases/StraightStair';
import LStair from '../components/staircases/LStair';
import UStair from '../components/staircases/UStair';
import SpiralStair from '../components/staircases/SpiralStair';
import BespokeSpiralStair from '../components/staircases/BespokeSpiralStair';

export class StairStateManager {
    constructor(mainSceneSetup) {
        this.mainSceneSetup = mainSceneSetup;
        this.isLoading = false;
        this.scene = mainSceneSetup.scene;
        
        // Track tread counts for different stair types
        this.treadCounts = {
            straight: 14,
            spiral: 14,
            lshaped: {
                bottom: 8,
                top: 8
            },
            ushaped: {
                bottom: 8,
                top: 8
            }
        };
    }

    getStairState() {
        return {
            type: this.currentType,
            attachment: this.currentAttachment,
            treadCounts: {
                total: this.getTotalTreads(),
                bottom: this.treadCounts.lshaped.bottom,
                top: this.treadCounts.lshaped.top
            }
        };
    }

    async handleTypeChange(newType) {
        // If already transitioning, completely abort
        if (this.isLoading) {
            console.log('Previous staircase still loading, aborting transition');
            return;
        }

        console.log(`Transitioning from ${this.currentType} to ${newType}`);
        this.isLoading = true;
        this.currentType = newType; // Track current type immediately

        try {
            const uiValues = this.getCurrentUIValues();
            
            // If current staircase exists and has a ready promise, await it
            if (this.mainSceneSetup.currentStaircase?.ready) {
                await this.mainSceneSetup.currentStaircase.ready;
            }
            
            await this.cleanupCurrentStaircase();
            await this.loadNewStaircase(newType, uiValues);
            
            console.log('Stair transition complete:', {
                type: this.mainSceneSetup.currentStairType,
                treads: this.mainSceneSetup.numberOfTreads
            });
        } catch (error) {
            console.error('Error during stair type transition:', error);
            // On error, reset the type to ensure state consistency
            this.currentType = this.mainSceneSetup.currentStairType;
        } finally {
            this.isLoading = false;
        }
    }

    handleAttachmentChange(newAttachment) {
        console.log(`Changing attachment from ${this.currentAttachment} to ${newAttachment}`);
        
    }

    getCurrentUIValues() {
        return {
            width: parseInt(document.getElementById('widthSlider').value, 10),
            straightTreads: parseInt(document.getElementById('stair-slider').value, 10),
            bottomTreads: parseInt(document.getElementById('bottom-flight-slider').value, 10),
            topTreads: parseInt(document.getElementById('top-flight-slider').value, 10),
            cantileverOption: document.querySelector('input[name="cantilever-option"]:checked').value,
            isCantilever: document.querySelector('input[name="cantilever-option"]:checked').value === 'cantilever'
        };
    }

    async loadNewStaircase(type) {
        const modelPath = this.mainSceneSetup.getModelPathForStaircase(type);
        const gltf = await this.loadModel(modelPath);
        const model = gltf.scene;
        const components = this.processModel(model);
        const uiState = this.mainSceneSetup.getUIState();
    
        if (this.mainSceneSetup.basePlate) {
            this.mainSceneSetup.basePlate.visible = !['Basic-Spiral', 'Bespoke-Spiral'].includes(type);
        }
    
        // Create new staircase instance with UI values
        this.mainSceneSetup.currentStaircase = await this.createStaircaseInstance(type, model, uiState);
        //update stair type in pricing calculator
        this.mainSceneSetup.currentStairType = type;

        // Assign components if they exist
        if (this.mainSceneSetup.currentStaircase) {
            await this.mainSceneSetup.currentStaircase.initialize();
            Object.entries(components).forEach(([key, value]) => {
                if (value) this.mainSceneSetup.currentStaircase[key] = value;
            });
        }
    
        await this.mainSceneSetup.updateControlsTarget();
        await this.mainSceneSetup.pricingCalculator.setStairType(
            type, 
            this.mainSceneSetup.currentStaircase, 
            uiState.cantileverOption === 'cantilever'
        );
        this.mainSceneSetup.calculatePrice();
    
        if (!type.includes('Spiral')) {
            this.mainSceneSetup.applyCantileverToggle();
        }
    }

    async loadModel(modelPath) {
        return new Promise((resolve, reject) => {
            this.mainSceneSetup.loader.load(modelPath, 
                (gltf) => resolve(gltf),
                undefined,
                (error) => reject(error)
            );
        });
    }

    processModel(model) {
        let components = {
            centralStringer: null,
            stub: null,
            treadPlate: null
        };

        model.traverse((child) => {
            if (child.isMesh) {
                if (child.name.includes('Central') && child.name.includes('Stringer')) {
                    components.centralStringer = child;
                } else if (child.name.includes('Stub')) {
                    components.stub = child;
                } else if (child.name.includes('Tread') && child.name.includes('Plate')) {
                    components.treadPlate = child;
                }
                child.castShadow = true;
                child.receiveShadow = true;
            }
        });

        return components;
    }

    async cleanupCurrentStaircase() {
        const currentStaircase = this.mainSceneSetup.currentStaircase;
        if (!currentStaircase) return;

        // Clean up tread group
        if (currentStaircase.treadGroup) {
            await this.cleanupGroup(currentStaircase.treadGroup);
            this.scene.remove(currentStaircase.treadGroup);
        }

        // Clean up spiral landing
        if (currentStaircase.walls?.spiralLanding) {
            this.scene.remove(currentStaircase.walls.spiralLanding);
            currentStaircase.walls.spiralLanding = null;
        }

        // Clean up walls
        if (currentStaircase.walls) {
            await this.cleanupWalls(currentStaircase.walls);
        }

        this.mainSceneSetup.currentStaircase = null;
    }

    async cleanupGroup(group) {
        group.children.forEach(child => {
            if (child.geometry) {
                child.geometry.dispose();
            }
            if (child.material) {
                if (Array.isArray(child.material)) {
                    child.material.forEach(mat => mat.dispose());
                } else {
                    child.material.dispose();
                }
            }
        });
    }

    async cleanupWalls(walls) {
        if (walls.backWall) {
            // Dispose of materials before removing
            if (Array.isArray(walls.backWall.material)) {
                walls.backWall.material.forEach(mat => {
                    if (mat.map) mat.map.dispose();
                    mat.dispose();
                });
            }
            this.scene.remove(walls.backWall);
        }
        if (walls.sideWall) {
            if (Array.isArray(walls.sideWall.material)) {
                walls.sideWall.material.forEach(mat => {
                    if (mat.map) mat.map.dispose();
                    mat.dispose();
                });
            }
            this.scene.remove(walls.sideWall);
        }
    }

    async createStaircaseInstance(type, model, uiState) {
        switch (type) {
            case 'straight':
                const straightStair = new StraightStair(this.scene, model, this.mainSceneSetup);
                await straightStair.updateTreadWidth(uiState.width);
                await straightStair.updateStairTreads(uiState.straightTreads);
                return straightStair;
    
            case 'L-shaped':
                const lStair = new LStair(this.scene, model, this.mainSceneSetup);
                await lStair.updateTreadWidth(uiState.width);
                await lStair.updateStairTreads(uiState.bottomTreads, uiState.topTreads);
                return lStair;
    
            case 'U-shaped':
                const uStair = new UStair(this.scene, model, this.mainSceneSetup);
                await uStair.updateTreadWidth(uiState.width);
                await uStair.updateStairTreads(uiState.bottomTreads, uiState.topTreads);
                return uStair;
    
            case 'Basic-Spiral':
                const spiralStair = new SpiralStair(this.scene, model, this.mainSceneSetup);
                await spiralStair.updateStairTreads(uiState.straightTreads);
                return spiralStair;
    
            case 'Bespoke-Spiral':
                const bespokeSpiral = new BespokeSpiralStair(this.scene, model, this.mainSceneSetup);
                await bespokeSpiral.updateStairTreads(uiState.straightTreads);
                return bespokeSpiral;
    
            default:
                console.error('Unknown staircase type:', type);
                return null;
        }
    }
}